import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  type InventoryLotReservationAllocation,
  InventoryLotReservationAllocationSchema,
} from "./inventory-lot.js";

const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
// Runtime validation below enforces canonical ISO timestamps. Keeping the
// structural schema format-free also makes this module safe when consumed
// without a process-global TypeBox format registry.
const DateTimeSchema = Type.String({ minLength: 24, maxLength: 40 });
const PositiveIntegerSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const NonNegativeIntegerSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const ChineseTextSchema = Type.String({ minLength: 1, maxLength: 300 });

export const KioskProductCatalogItemSchema = Type.Object(
  {
    skuId: StableIdSchema,
    productVersion: PositiveIntegerSchema,
    name: Type.String({ minLength: 1, maxLength: 120 }),
    unit: Type.String({ minLength: 1, maxLength: 32 }),
    unitPriceFen: NonNegativeIntegerSchema,
    inventoryQuantity: NonNegativeIntegerSchema,
    reservedQuantity: NonNegativeIntegerSchema,
    expiredQuantity: Type.Optional(NonNegativeIntegerSchema),
    availableQuantity: NonNegativeIntegerSchema,
    canOrder: Type.Boolean(),
    unavailableReason: Type.Union([ChineseTextSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type KioskProductCatalogItem = Static<typeof KioskProductCatalogItemSchema>;

export const KioskProductCatalogReadModelSchema = Type.Object(
  {
    tableNodeId: StableIdSchema,
    sessionId: StableIdSchema,
    items: Type.Array(KioskProductCatalogItemSchema, { maxItems: 500 }),
    generatedAt: DateTimeSchema,
  },
  { additionalProperties: false },
);
export type KioskProductCatalogReadModel = Static<typeof KioskProductCatalogReadModelSchema>;

export const CreateKioskProductReservationCommandSchema = Type.Object(
  {
    tableNodeId: StableIdSchema,
    sessionId: StableIdSchema,
    skuId: StableIdSchema,
    productVersion: PositiveIntegerSchema,
    quantity: Type.Integer({ minimum: 1, maximum: 99 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type CreateKioskProductReservationCommand = Static<
  typeof CreateKioskProductReservationCommandSchema
>;

export const KioskProductReservationStateSchema = Type.Union([
  Type.Literal("RESERVED"),
  Type.Literal("ACKNOWLEDGED"),
  Type.Literal("DELIVERED"),
  Type.Literal("CANCELLED"),
  Type.Literal("FAILED"),
]);
export type KioskProductReservationState = Static<typeof KioskProductReservationStateSchema>;

export const KioskProductReservationReadModelSchema = Type.Object(
  {
    reservationId: StableIdSchema,
    reservationVersion: PositiveIntegerSchema,
    tableNodeId: StableIdSchema,
    tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
    sessionId: StableIdSchema,
    orderId: StableIdSchema,
    skuId: StableIdSchema,
    productVersion: PositiveIntegerSchema,
    productName: Type.String({ minLength: 1, maxLength: 120 }),
    unit: Type.String({ minLength: 1, maxLength: 32 }),
    unitPriceFen: NonNegativeIntegerSchema,
    quantity: Type.Integer({ minimum: 1, maximum: 99 }),
    lineTotalFen: NonNegativeIntegerSchema,
    state: KioskProductReservationStateSchema,
    requestedAt: DateTimeSchema,
    acknowledgedAt: Type.Union([DateTimeSchema, Type.Null()]),
    deliveredAt: Type.Union([DateTimeSchema, Type.Null()]),
    cancelledAt: Type.Union([DateTimeSchema, Type.Null()]),
    updatedAt: DateTimeSchema,
    orderItemId: Type.Union([StableIdSchema, Type.Null()]),
    failureReason: Type.Union([ChineseTextSchema, Type.Null()]),
    lotAllocations: Type.Optional(
      Type.Array(InventoryLotReservationAllocationSchema, { minItems: 1, maxItems: 100 }),
    ),
  },
  { additionalProperties: false },
);
export type KioskProductReservationReadModel = Static<
  typeof KioskProductReservationReadModelSchema
>;

export const KioskProductReservationBusinessEventPayloadSchema = Type.Object(
  { reservation: KioskProductReservationReadModelSchema },
  { additionalProperties: false },
);
export type KioskProductReservationBusinessEventPayload = Static<
  typeof KioskProductReservationBusinessEventPayloadSchema
>;

export const KioskProductReservationListReadModelSchema = Type.Object(
  {
    tableNodeId: StableIdSchema,
    sessionId: StableIdSchema,
    items: Type.Array(KioskProductReservationReadModelSchema, { maxItems: 100 }),
    generatedAt: DateTimeSchema,
  },
  { additionalProperties: false },
);
export type KioskProductReservationListReadModel = Static<
  typeof KioskProductReservationListReadModelSchema
>;

export const KioskProductReservationTransitionCommandSchema = Type.Object(
  {
    reservationId: StableIdSchema,
    expectedReservationVersion: PositiveIntegerSchema,
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type KioskProductReservationTransitionCommand = Static<
  typeof KioskProductReservationTransitionCommandSchema
>;

export const CancelKioskProductReservationCommandSchema = Type.Object(
  {
    ...KioskProductReservationTransitionCommandSchema.properties,
    reason: ChineseTextSchema,
  },
  { additionalProperties: false },
);
export type CancelKioskProductReservationCommand = Static<
  typeof CancelKioskProductReservationCommandSchema
>;

export const DeliverKioskProductReservationReceiptSchema = Type.Object(
  {
    reservation: KioskProductReservationReadModelSchema,
    orderId: StableIdSchema,
    orderItemId: StableIdSchema,
    inventoryQuantityAfter: NonNegativeIntegerSchema,
  },
  { additionalProperties: false },
);
export type DeliverKioskProductReservationReceipt = Static<
  typeof DeliverKioskProductReservationReceiptSchema
>;

export function isKioskProductCatalogReadModel(
  value: unknown,
): value is KioskProductCatalogReadModel {
  if (!Value.Check(KioskProductCatalogReadModelSchema, value)) return false;
  const generatedAt = Date.parse(value.generatedAt);
  if (
    !Number.isFinite(generatedAt) ||
    new Date(generatedAt).toISOString() !== value.generatedAt ||
    new Set(value.items.map((item) => item.skuId)).size !== value.items.length
  ) {
    return false;
  }
  return value.items.every(
    (item) =>
      item.reservedQuantity + (item.expiredQuantity ?? 0) <= item.inventoryQuantity &&
      item.availableQuantity ===
        item.inventoryQuantity - item.reservedQuantity - (item.expiredQuantity ?? 0) &&
      item.canOrder === (item.availableQuantity > 0 && item.unavailableReason === null),
  );
}

export function isKioskProductReservationReadModel(
  value: unknown,
): value is KioskProductReservationReadModel {
  if (!Value.Check(KioskProductReservationReadModelSchema, value)) return false;
  const expectedLineTotalFen = value.unitPriceFen * value.quantity;
  const lotAllocations = value.lotAllocations as InventoryLotReservationAllocation[] | undefined;
  const lotQuantity = lotAllocations?.reduce(
    (total, allocation) => total + allocation.reservedBaseQuantity,
    0,
  );
  if (!Number.isSafeInteger(expectedLineTotalFen) || value.lineTotalFen !== expectedLineTotalFen) {
    return false;
  }
  if (
    lotAllocations !== undefined &&
    (new Set(lotAllocations.map((allocation) => allocation.lotId)).size !== lotAllocations.length ||
      !Number.isSafeInteger(lotQuantity) ||
      lotQuantity !== value.quantity)
  ) {
    return false;
  }
  const requestedAt = Date.parse(value.requestedAt);
  const updatedAt = Date.parse(value.updatedAt);
  const acknowledgedAt = value.acknowledgedAt === null ? null : Date.parse(value.acknowledgedAt);
  const deliveredAt = value.deliveredAt === null ? null : Date.parse(value.deliveredAt);
  const cancelledAt = value.cancelledAt === null ? null : Date.parse(value.cancelledAt);
  if (
    !Number.isFinite(requestedAt) ||
    !Number.isFinite(updatedAt) ||
    updatedAt < requestedAt ||
    (acknowledgedAt !== null &&
      (!Number.isFinite(acknowledgedAt) || acknowledgedAt < requestedAt)) ||
    (deliveredAt !== null &&
      (!Number.isFinite(deliveredAt) || acknowledgedAt === null || deliveredAt < acknowledgedAt)) ||
    (cancelledAt !== null && (!Number.isFinite(cancelledAt) || cancelledAt < requestedAt))
  ) {
    return false;
  }
  for (const [text, time] of [
    [value.requestedAt, requestedAt],
    [value.updatedAt, updatedAt],
    [value.acknowledgedAt, acknowledgedAt],
    [value.deliveredAt, deliveredAt],
    [value.cancelledAt, cancelledAt],
  ] as const) {
    if (text !== null && new Date(time as number).toISOString() !== text) return false;
  }
  switch (value.state) {
    case "RESERVED":
      return (
        acknowledgedAt === null &&
        deliveredAt === null &&
        cancelledAt === null &&
        value.orderItemId === null &&
        value.failureReason === null
      );
    case "ACKNOWLEDGED":
      return (
        acknowledgedAt !== null &&
        deliveredAt === null &&
        cancelledAt === null &&
        value.orderItemId === null &&
        value.failureReason === null
      );
    case "DELIVERED":
      return (
        acknowledgedAt !== null &&
        deliveredAt !== null &&
        cancelledAt === null &&
        value.orderItemId !== null &&
        value.failureReason === null
      );
    case "CANCELLED":
      return deliveredAt === null && cancelledAt !== null && value.orderItemId === null;
    case "FAILED":
      return (
        deliveredAt === null &&
        cancelledAt === null &&
        value.orderItemId === null &&
        value.failureReason !== null
      );
  }
}

export function isKioskProductReservationBusinessEventPayload(
  value: unknown,
): value is KioskProductReservationBusinessEventPayload {
  return (
    Value.Check(KioskProductReservationBusinessEventPayloadSchema, value) &&
    isKioskProductReservationReadModel(value.reservation)
  );
}

export function isKioskProductReservationListReadModel(
  value: unknown,
): value is KioskProductReservationListReadModel {
  if (!Value.Check(KioskProductReservationListReadModelSchema, value)) return false;
  if (!value.items.every(isKioskProductReservationReadModel)) return false;
  if (new Set(value.items.map((item) => item.reservationId)).size !== value.items.length)
    return false;
  return value.items.every((item, index) => {
    const previous = value.items[index - 1];
    return (
      item.tableNodeId === value.tableNodeId &&
      item.sessionId === value.sessionId &&
      (previous === undefined || Date.parse(previous.requestedAt) >= Date.parse(item.requestedAt))
    );
  });
}

export function isDeliverKioskProductReservationReceipt(
  value: unknown,
): value is DeliverKioskProductReservationReceipt {
  return (
    Value.Check(DeliverKioskProductReservationReceiptSchema, value) &&
    isKioskProductReservationReadModel(value.reservation) &&
    value.reservation.state === "DELIVERED" &&
    value.reservation.orderId === value.orderId &&
    value.reservation.orderItemId === value.orderItemId
  );
}

export function orderItemMatchesDeliveredKioskProductReservation(
  item: {
    id: string;
    sourceReservationId?: string;
    skuId: string;
    productVersion?: number;
    name: string;
    unitPriceFen: number;
    quantity: number;
    returnedQuantity: number;
  },
  reservation: KioskProductReservationReadModel,
): boolean {
  return (
    isKioskProductReservationReadModel(reservation) &&
    reservation.state === "DELIVERED" &&
    reservation.orderItemId === item.id &&
    item.sourceReservationId === reservation.reservationId &&
    item.skuId === reservation.skuId &&
    item.productVersion === reservation.productVersion &&
    item.name === reservation.productName &&
    item.unitPriceFen === reservation.unitPriceFen &&
    item.quantity === reservation.quantity &&
    item.returnedQuantity === 0
  );
}
